import { put, select, takeLatest } from "redux-saga/effects";
import * as globalApi from "Store/global/api";
import {
  fetchCategoriesApi,
  fetchCoursesApi,
  fetchLessonTemplateApi,
  fetchWorksheetTemplateApi, sendWorkbookEmailApi
} from "Store/global/api";
import * as globalActions from "Store/global/actions";
import { setCategoryTree } from "Store/global/actions";

import * as globalTypes from "Store/global/constants";
import * as globalSelectors from "Store/global/selectors";
import { Toast } from "utils/toast";
import { createFiltersOptions } from "utils/filtersHelper";
import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import { makeSelectActiveStudent } from "Store/students/selectors";
import { subjectsId } from "constants/subjects";
import { getFormattedCategories, sortByProperty } from "utils/sorting";
import * as classApi from "Store/classrooms/api";
import { fetchInstructorsApi } from "Store/instructors/api";
import { makeSelectCurrentUser } from "Store/user/selectors";
import { fetchLocationsApi } from "Store/locations/api";
import { getActiveCourseId, setActiveCourseId } from "utils/ActiveCourseService";
import { fetchStudentsApi } from "Store/students/api";
import { setInstructorsSimplifiedLoading } from "Store/instructors/actions";
import { setLocationsSimplifiedLoading } from "Store/locations/actions";
import { setClassroomsSimplifiedLoading } from "Store/classrooms/actions";
import { makeSelectActiveClassroom } from "Store/classrooms/selectors";

/* Helper functions */
const STUDENT_STATUS = {
  INACTIVE: "unactivated",
  ACTIVE: "current",
  COMPLETED: "completed"
};

const handleSortBy = (sortBy: string) => {
  if (!sortBy) return null;
  if (sortBy.includes("student_test_scores")) {
    return "score_subject=" + subjectsId[sortBy];
  } else if (sortBy.includes("students_enrollment")) {
    const course = sortBy.split("students_enrollment_")[1].toUpperCase();
    return `sort_by_course=${course}`;
  } else {
    return (
      "sort_by=" +
      sortBy +
      (sortBy === "problem_flag_count" ||
      sortBy === "reviewed_problems" ||
      sortBy === "video_watched_time"
        ? "&disable_sort_status=1"
        : "")
    );
  }
};

export function* renderApiParameters(selector, type: string) {
  const pagination = yield select(
    selector[`makeSelect${type[0].toUpperCase() + type.slice(1)}Pagination`]
  );

  const activeStudent = yield select(makeSelectActiveStudent);
  const activeClassroom = yield select(makeSelectActiveClassroom);

  if (pagination.review_statuses) {
    pagination.flagged_to_review = false;
    pagination.reviewed_problems = false;
  }
  // Create string from pagination
  const query = {
    page: pagination.page ? "page=" + pagination.page : null,
    search: pagination.search ? "search=" + pagination.search : null,
    per_page: pagination.per_page ? "per_page=" + pagination.per_page : null,
    sort_by: handleSortBy(pagination.sort_by),
    sort_type: pagination.sort_type ? "sort_type=" + pagination.sort_type : null,
    test_type: pagination.test_type ? "test_type=" + pagination.test_type : null,
    stats: pagination.stats ? "stats=" + pagination.stats : null,
    subject_id: pagination.subjects ? "subject_id=" + pagination.subjects : null,
    unit_id: pagination.units ? "unit_id=" + pagination.units : null,
    lesson_type: pagination.lesson_types ? "lesson_type=" + pagination.lesson_types : null,
    template_type: pagination.template_type ? "type=" + pagination.template_type : null,

    // Worksheet parameters:
    category_id: pagination.categories ? "category_id=" + pagination.categories : null,
    worksheet_type: pagination.worksheet_types
      ? "worksheet_type=" + pagination.worksheet_types
      : null,
    worksheet_content_type: pagination.content_types
      ? "worksheet_content_type=" + pagination.content_types
      : null,
    status:
      pagination.statuses && pagination.statuses.length > 0
        ? "status=" + pagination.statuses.map((s) => s.value).join(",")
        : null,
    review_statuses:
      pagination.review_statuses && pagination.review_statuses.length > 0
        ? pagination.review_statuses.map((rs) => (pagination[rs.value] = true))
        : null,
    due_status: pagination.due_statuses ? "due=" + pagination.due_statuses.toLowerCase() : null,
    instructors: pagination.instructors ? "instructor=" + pagination.instructors : null,
    classrooms: pagination.classrooms ? "classroom=" + pagination.classrooms : null,
    classrooms_archived: pagination.classrooms_archived ? "archived=1" : null,
    locations: pagination.locations ? "location=" + pagination.locations : null,
    student_archived: pagination.student_archived ? "show_archived=1" : null,
    student_hit_target_score: pagination.student_hit_target_score
      ? "hit_target_score=" + (pagination.student_hit_target_score === "hit" ? 1 : 0)
      : null,
    flagged_to_review: pagination.flagged_to_review ? "flagged=1" : null,
    reviewed_problems: pagination.reviewed_problems ? "reviewed=1" : null,
    reviewed: pagination.reviewed ? "reviewed=1" : null,
    without_student_id: pagination.without_student_id
      ? "without_student_id=" + activeStudent.id
      : null,
    all: pagination.all ? "all" : null,
    has_overdue_work: pagination.has_overdue_work ? "has_overdue_work=1" : null,
    student_status: pagination.student_status
      ? "status=" + STUDENT_STATUS[pagination.student_status]
      : null,
    start_date: pagination.start_date ? `start_date=${pagination.start_date}` : null,
    end_date: pagination.end_date ? `end_date=${pagination.end_date}` : null,
    classroom_tests_completed: pagination.classroom_tests_completed ? "completed=1" : null,
    instructors_archived: pagination.instructors_archived ? "archived=1" : null,
    template_archived: pagination.template_archived ? "is_active=0" : "is_active=1",
    without_classroom_id: pagination.without_classroom_id
      ? "without_classroom_id=" + activeClassroom.id
      : null
  };
  delete query.review_statuses;
  const s = Object.keys(query)
    .map((key) => (query[key] ? `${query[key]}` : null))
    .filter((k) => k !== null)
    .join("&")
    .replace(/[#]/g, "%23");
  return "?" + s;
}

export const convertMetaToPagination = (meta) => {
  return {
    page: meta.current_page,
    per_page: meta.per_page,
    total: meta.total,
    last_page: meta.last_page,
    from: meta.from,
    to: meta.to
  };
};

export function* downloadFile(file: Blob, filename: string) {
  const url = yield window.URL.createObjectURL(file);
  const link = yield document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename); // or any other extension
  yield document.body.appendChild(link);
  yield link.click();
  link.parentNode.removeChild(link);
}

/* GENERATORS */
function* handleSendWelcomeEmail(action) {
  try {
    const { error, data } = yield globalApi.sendWelcomeEmailApi(action.body);
    if (data.user_id) {
      Toast.success(SUCCESSES.handleSendWelcomeEmail);
    } else {
      Toast.error(ERRORS.handleSendWelcomeEmail, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleSendWelcomeEmail, error);
  }
}
function* handleSendWorkbookEmail(action) {
  try {
    const { error, data } = yield globalApi.sendWorkbookEmailApi(action.body);
    if (data.user_id) {
      Toast.success(SUCCESSES.handleSendWelcomeEmail);
    } else {
      Toast.error(ERRORS.handleSendWelcomeEmail, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleSendWelcomeEmail, error);
  }
}

function* handleFetchAllTestsTemplates() {
  yield put(globalActions.setFetchSublistSpinner(true));
  try {
    const { data, error } = yield globalApi.fetchAllTestsTemplatesApi();
    if (error) {
      Toast.error(ERRORS.handleFetchAllTestsTemplates, error);
    } else {
      yield put(globalActions.setTestsTemplates(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchAllTestsTemplates, error);
  } finally {
    yield put(globalActions.setFetchSublistSpinner(false));
  }
}

function* handleFetchLessonsTemplates() {
  yield put(globalActions.setGlobalTemplateLoader(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "lessons");
    const { data: lessons, meta } = yield globalApi.fetchLessonsTemplatesApi(parameters);
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "lessons"));

    yield put(globalActions.setLessonsTemplates(lessons));
  } catch (e) {
    Toast.error(ERRORS.handleFetchLessonsTemplates, e.message);
  } finally {
    yield put(globalActions.setGlobalTemplateLoader(false));
  }
}

/* FILTERS */
const createCategoryTree = (categories, parentId) =>
  categories
    .filter((subcat) => subcat.parent_id === parentId)
    .map((subcat) => ({
      ...subcat,
      children: createCategoryTree(categories, subcat.id)
    }));

function* handleFetchWorksheetsFilterItemsSaga() {
  try {
    const { data: contentTypes } = yield globalApi.fetchWorksheetsContentTypesApi();
    const { data: categories } = yield fetchCategoriesApi();
    const { data: subjects } = yield globalApi.fetchWorksheetsSubjectsApi();
    const categoriesTree = createCategoryTree(categories, null);

    yield put(setCategoryTree(categoriesTree));

    const worksheetCategoriesFormatted = getFormattedCategories(categories);

    const worksheetFilters = [
      createFiltersOptions(
        "content_types",
        contentTypes.map((cT) => ({
          value: cT,
          label: cT.split("_").join(" ").toLowerCase()
        }))
      ),
      createFiltersOptions("categories", worksheetCategoriesFormatted),
      createFiltersOptions(
        "subjects",
        subjects.map((c) => ({ value: c.id, label: c.name }))
      )
    ];

    yield put(globalActions.setWorksheetsFilterItems(worksheetFilters));
  } catch (error) {
    Toast.error("handleFetchWorksheetsFilterItemsSaga Error", error.message);
  }
}

function* handleFetchLessonsFilterItemsSaga() {
  try {
    const { data: types } = yield globalApi.fetchLessonsTypesApi();
    const { data: units } = yield globalApi.fetchLessonsCategoriesApi();
    const { data: subjects } = yield globalApi.fetchLessonsSubjectsApi();

    const lessonFilters = [
      createFiltersOptions(
        "lesson_types",
        types.map((c) => ({
          value: c.id,
          label: c.label
        }))
      ),
      createFiltersOptions(
        "units",
        units.map((c) => ({ value: c.id, label: c.name }))
      ),
      createFiltersOptions(
        "subjects",
        subjects.map((c) => ({ value: c.id, label: c.name }))
      )
    ];
    yield put(globalActions.setLessonsFilterItems(lessonFilters));
  } catch (error) {
    Toast.error(error);
  }
}

function* handleFetchWorksheetsTemplates() {
  yield put(globalActions.setGlobalTemplateLoader(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "Worksheets");
    const { data: worksheets, meta } = yield globalApi.fetchWorksheetsTemplatesApi(parameters);
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "worksheets"));
    yield put(globalActions.setWorksheetsTemplates(worksheets));
  } catch (error) {
    Toast.error(error);
  } finally {
    yield put(globalActions.setGlobalTemplateLoader(false));
  }
}

function* handleFetchSubjects() {
  try {
    const { data } = yield globalApi.fetchSubjectsApi();
    yield put(globalActions.setSubjects(data.subjects));
  } catch (error) {
    Toast.error(ERRORS.handleFetchSubjects, error);
  }
}

function* handleFetchLessonTemplateDetailsSaga(action) {
  try {
    let fullLesson;
    let problems = {};
    const { data, error } = yield fetchLessonTemplateApi(action.payload);

    if (error) {
      return Toast.error(ERRORS.handleFetchLessonPreviewClassDetail, error);
    }
    fullLesson = data;

    const label = data.type.label;

    if (label === "Drill") {
      problems = { drill: data.problems };
    }
    if (fullLesson.sections) {
      fullLesson.sections.map((section) => {
        problems = {
          ...problems,
          [section.name]: section.lesson_problems
        };
      });
    }
    yield put(globalActions.setLessonTemplatePreview({ ...fullLesson, problems })); // Needs to be completely changed and set as not student lesson but active template
  } catch (error) {
    Toast.error(ERRORS.handleFetchLessonTemplateDetailsSaga, error);
  } finally {
  }
}

function* handleFetchWorksheetTemplateDetailsSaga(action) {
  try {
    const worksheetTemplate = yield fetchWorksheetTemplateApi(action.payload);
    yield put(globalActions.setWorksheetTemplatePreview(worksheetTemplate.data));
  } catch (error) {
    Toast.error(ERRORS.handleFetchLessonTemplateDetailsSaga, error);
  } finally {
  }
}

function* handleFetchLessonsHintsSaga() {
  yield put(globalActions.setFetchSublistSpinner(true));
  try {
    const { data, error } = yield globalApi.fetchLessonsHintsApi();
    if (error) {
      Toast.error(ERRORS.handleFetchLessonsHintsSaga, error);
    } else {
      const sortedNames = sortByProperty(data, "name");
      yield put(globalActions.setLessonsHints(sortedNames));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchLessonsHintsSaga, error);
  } finally {
    yield put(globalActions.setFetchSublistSpinner(false));
  }
}

function* handleFetchWorksheetsHintsSaga() {
  yield put(globalActions.setFetchSublistSpinner(true));
  try {
    const { data, error } = yield globalApi.fetchWorksheetsHintsApi();
    if (error) {
      Toast.error(ERRORS.handleFetchWorksheetsHintsSaga, error);
    } else {
      const sortedNames = sortByProperty(data, "title");
      yield put(globalActions.setWorksheetsHints(sortedNames));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchWorksheetsHintsSaga, error);
  } finally {
    yield put(globalActions.setFetchSublistSpinner(false));
  }
}

function* handleFetchUnitsSaga() {
  try {
    const { data } = yield globalApi.fetchUnitsApi();
    const formattedUnits = data
      .map(({ name, id }) => ({
        label: name,
        value: id
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
    yield put(globalActions.setUnitFilterOptions(formattedUnits));
  } catch (err) {
    console.warn(ERRORS.handleFetchUnitsSaga, err);
  }
}

function* handleFetchSimplifiedClassroomsSaga(action) {
  yield put(setClassroomsSimplifiedLoading(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "classrooms");
    const query = `${action.all ? "?all=1" : parameters}&simplified=1`;
    const { meta, data, error } = yield classApi.fetchClassroomsApi(query);

    if (!action.all) {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "classrooms"));
    }
    if (error) {
      Toast.error(ERRORS.handleFetchSimpClassrooms, error);
    } else {
      yield put(globalActions.setClassroomsSimplified(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchSimpClassrooms, error);
  } finally {
    yield put(setClassroomsSimplifiedLoading(false));
  }
}

function* handleFetchSimplifiedInstructorsSaga(action) {
  try {
    yield put(setInstructorsSimplifiedLoading(true));
    const parameters = yield renderApiParameters(globalSelectors, "instructors");
    const query = `${action.all ? "?all=1" : parameters}&simplified=1&sort_by=name&sort_order=asc`;
    const { meta, data, error } = yield fetchInstructorsApi(query);
    if (!action.all) {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "instructors"));
    }

    if (error) {
      Toast.error(ERRORS.handleFetchSimpInstructors, error);
    } else {
      yield put(globalActions.setInstructorsSimplified(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchSimpInstructors, error);
  } finally {
    yield put(setInstructorsSimplifiedLoading(false));
  }
}

function* handleFetchSimplifiedLocationsSaga(action) {
  yield put(setLocationsSimplifiedLoading(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "locations");
    const currentUser = yield select(makeSelectCurrentUser);

    const query = `${action.all ? "?all=1" : parameters}&simplified=1`;

    const { meta, data, error } = yield fetchLocationsApi(currentUser?.id, query);

    if (!action.all) {
      yield put(globalActions.setPagination(convertMetaToPagination(meta), "locations"));
    }

    if (error) {
      Toast.error(ERRORS.handleFetchSimplifiedLocations, error);
    } else {
      yield put(globalActions.setLocationsSimplified(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchSimplifiedLocations, error);
  } finally {
    yield put(setLocationsSimplifiedLoading(false));
  }
}

function* handleFetchSimplifiedStudentsSaga() {
  yield put(globalActions.setFetchSublistSpinner(true));
  try {
    const parameters = yield renderApiParameters(globalSelectors, "students");
    const query = `${parameters}&simplified=1`;

    const { meta, data, error } = yield fetchStudentsApi(query);
    yield put(globalActions.setPagination(convertMetaToPagination(meta), "students"));
    if (error) {
      Toast.error(ERRORS.handleFetchSimplifiedStudentsSaga, error);
    } else {
      yield put(globalActions.setStudentsSimplified(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchSimplifiedStudentsSaga, error);
  } finally {
    yield put(globalActions.setFetchSublistSpinner(false));
  }
}

export function* handleFetchCoursesSaga() {
  try {
    const { data, error } = yield fetchCoursesApi();
    if (error) {
      return Toast.error(ERRORS.handleGetCourses, error);
    }
    const allCourses = {
      course_id: "all_courses",
      course_name: "All"
    };

    const courses = [...data, allCourses];
    const currentCourse = courses.find((course) => course.course_id === getActiveCourseId());
    yield put(globalActions.setActiveCourse(currentCourse || courses[0]));
    if (!currentCourse) setActiveCourseId(data[0].course_id);
    yield put(globalActions.setCourses(courses));
  } catch (error) {
    Toast.error(ERRORS.handleGetCourses, error);
  }
}

export default function* globalSaga() {
  yield takeLatest(globalTypes.SEND_WELCOME_EMAIL, handleSendWelcomeEmail);
  yield takeLatest(globalTypes.SEND_WORKBOOK_EMAIL, handleSendWorkbookEmail);
  yield takeLatest(globalTypes.HANDLE_FETCH_ALL_TESTS_TEMPLATES, handleFetchAllTestsTemplates);
  yield takeLatest(globalTypes.HANDLE_FETCH_LESSONS_TEMPLATES, handleFetchLessonsTemplates);
  yield takeLatest(globalTypes.HANDLE_FETCH_WORKSHEETS_TEMPLATES, handleFetchWorksheetsTemplates);
  yield takeLatest(globalTypes.HANDLE_FETCH_SUBJECTS, handleFetchSubjects);
  yield takeLatest(
    globalTypes.HANDLE_FETCH_WORKSHEETS_FILTER_ITEMS,
    handleFetchWorksheetsFilterItemsSaga
  );
  yield takeLatest(
    globalTypes.HANDLE_FETCH_LESSONS_FILTER_ITEMS,
    handleFetchLessonsFilterItemsSaga
  );
  yield takeLatest(
    globalTypes.HANDLE_FETCH_LESSON_TEMPLATE_DETAILS,
    handleFetchLessonTemplateDetailsSaga
  );
  yield takeLatest(
    globalTypes.HANDLE_FETCH_WORKSHEET_TEMPLATE_DETAILS,
    handleFetchWorksheetTemplateDetailsSaga
  );
  yield takeLatest(globalTypes.HANDLE_FETCH_LESSONS_HINTS, handleFetchLessonsHintsSaga);
  yield takeLatest(globalTypes.HANDLE_FETCH_WORKSHEETS_HINTS, handleFetchWorksheetsHintsSaga);

  yield takeLatest(
    globalTypes.HANDLE_FETCH_CLASSROOMS_SIMPLIFIED,
    handleFetchSimplifiedClassroomsSaga
  );
  yield takeLatest(
    globalTypes.HANDLE_FETCH_INSTRUCTORS_SIMPLIFIED,
    handleFetchSimplifiedInstructorsSaga
  );
  yield takeLatest(
    globalTypes.HANDLE_FETCH_LOCATIONS_SIMPLIFIED,
    handleFetchSimplifiedLocationsSaga
  );
  yield takeLatest(globalTypes.HANDLE_FETCH_STUDENTS_SIMPLIFIED, handleFetchSimplifiedStudentsSaga);
  yield takeLatest(globalTypes.HANDLE_FETCH_COURSES, handleFetchCoursesSaga);
  // TO BE REMOVED
  yield takeLatest(globalTypes.HANDLE_FETCH_UNITS, handleFetchUnitsSaga);
}
